<template>
  <SoMenu
    v-model="isOpen"
    :autoclose="false"
    :loading="loading"
    :max-width="filterWidth"
    icon="filter"
    label="All Filters"
    class="so-data-table-filters"
    data-testid="so-data-table-filters-btn"
    large
  >
    <SoForm class="px-5 pb-0" @submit="applyFilters()">
      <div class="d-flex align-center justify-space-between">
        <h4 class="font-weight-thin">All Filters</h4>
        <v-card-actions class="mr-n5">
          <v-btn icon="close" variant="plain" @click.stop="close()"></v-btn>
        </v-card-actions>
      </div>
      <div class="so-data-table-filters__list pb-3">
        <SoExpansionPanels type="accordion">
          <template
            v-for="(filter, filterName) in innerFilters"
            :key="filterName"
          >
            <component
              :is="!filter.expandable ? 'div' : 'SoExpansionPanel'"
              :class="
                !filter.expandable ? 'so-data-table-filters__option px-2' : ''
              "
            >
              <template #title>{{ filter.label }}</template>

              <template v-if="filter.type === FilterType.RADIO">
                <SoCheckbox
                  v-for="(label, option) in filter.options"
                  :key="option"
                  v-model="filter.value"
                  :label="label"
                  :value="option"
                  :false-value="null"
                />
              </template>

              <template v-if="filter.type === FilterType.CHECKBOX">
                <SoCheckbox
                  v-for="(label, option) in filter.options"
                  :key="option"
                  v-model="filter.value"
                  :label="!filter.expandable ? filter.label : label"
                  :class="!filter.expandable ? 'mt-3' : ''"
                  :value="option"
                  :false-value="null"
                  :truncate-text="filter.truncateText"
                  multiple
                />
              </template>
              <template v-else-if="filter.type === FilterType.DATE_RANGE">
                <div class="d-flex gap-2">
                  <SoDatePicker
                    v-model="filter.value.from"
                    :label="filter.fromLabel ?? 'From'"
                    type="date"
                    clearable
                    class="flex-1-0"
                    @update:model-value="validateDateRange(filterName, 'from')"
                  />
                  <SoDatePicker
                    v-model="filter.value.to"
                    :label="filter.toLabel ?? 'To'"
                    type="date"
                    clearable
                    class="flex-1-0"
                    @update:model-value="validateDateRange(filterName, 'to')"
                  />
                </div>
              </template>
              <template v-else-if="filter.type === FilterType.DATE_TIME_RANGE">
                <SoDatePicker
                  v-model="filter.value"
                  :label="filter.label"
                  type="datetimerange"
                  class="mb-1"
                />
              </template>
              <template v-else-if="filter.type === FilterType.MONEY_RANGE">
                <div class="d-flex gap-2">
                  <SoTextField
                    v-model="filter.value.min"
                    :label="filter.minLabel ?? 'Min £'"
                    type="number"
                    @blur="validateNumberRange(filterName, 'min')"
                  />
                  <SoTextField
                    v-model="filter.value.max"
                    :label="filter.maxLabel ?? 'Max £'"
                    type="number"
                    @blur="validateNumberRange(filterName, 'max')"
                  />
                </div>
              </template>
              <template v-else-if="filter.type === FilterType.NUMBER_RANGE">
                <div class="d-flex gap-2">
                  <SoTextField
                    v-model="filter.value.min"
                    label="Min"
                    type="number"
                    @blur="validateNumberRange(filterName, 'min')"
                  />
                  <SoTextField
                    v-model="filter.value.max"
                    label="Max"
                    type="number"
                    @blur="validateNumberRange(filterName, 'max')"
                  />
                </div>
              </template>
            </component>
          </template>
        </SoExpansionPanels>
      </div>
      <template #actions>
        <div class="d-flex align-end justify-end pb-2">
          <SoButton title="reset" @click="reset()" />
          <SoButton
            title="Apply Filters"
            type="success"
            class="ml-2 mt-3"
            :disabled="!hasChanges"
            submit
          />
        </div>
      </template>
    </SoForm>
  </SoMenu>
</template>

<script lang="ts">
  import SoMenu from "@/components/SoMenu.vue"
  import SoExpansionPanel from "@/components/expansionPanels/SoExpansionPanel.vue"
  import SoExpansionPanels from "@/components/expansionPanels/SoExpansionPanels.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoCheckbox from "@/components/formControls/SoCheckbox.vue"
  import SoDatePicker from "@/components/formControls/SoDatePicker.vue"
  import SoForm from "@/components/formControls/SoForm.vue"
  import SoTextField from "@/components/formControls/SoTextField.vue"
  import {
    FilterDateRange,
    FilterMoneyRange,
    FilterNumberRange,
    FilterType,
    SoDataTableFilters,
  } from "@/types/components/SoDataTable"
  import dayjs from "dayjs"
  import { cloneDeep } from "lodash-es"
  import { PropType } from "vue"
  import { defineComponent } from "vue"
  import SoSelect from "@/components/formControls/SoSelect.vue"

  export default defineComponent({
    components: {
      SoSelect,
      SoTextField,
      SoButton,
      SoMenu,
      SoForm,
      SoCheckbox,
      SoExpansionPanel,
      SoExpansionPanels,
      SoDatePicker,
    },

    props: {
      filters: {
        type: Object as PropType<SoDataTableFilters>,
        default: () => ({}),
      },
      loading: {
        type: Boolean,
        default: false,
      },
      filterWidth: {
        type: String,
        default: "400px",
      },
    },

    emits: ["update:filters"],
    data() {
      return {
        FilterType,
        isOpen: false,
        innerFilters: {} as SoDataTableFilters,
        dayjs,
      }
    },

    computed: {
      hasChanges() {
        return (
          JSON.stringify(this.filters) !== JSON.stringify(this.innerFilters)
        )
      },
    },

    watch: {
      filters: {
        handler() {
          this.copyFilters()
        },
        deep: true,
      },
    },

    created() {
      this.copyFilters()
    },

    methods: {
      copyFilters() {
        this.innerFilters = cloneDeep(this.filters)
      },
      applyFilters() {
        this.$emit("update:filters", this.innerFilters)
        this.close()
      },
      reset() {
        for (const filterName in this.innerFilters) {
          const filter = this.innerFilters[filterName]
          if (filter.type === FilterType.CHECKBOX) {
            filter.value = []
          } else if (filter.type === FilterType.DATE_RANGE) {
            filter.value = { from: null, to: null }
          } else if (filter.type === FilterType.DATE_TIME_RANGE) {
            filter.value = null
          } else if (
            filter.type === FilterType.MONEY_RANGE ||
            filter.type === FilterType.NUMBER_RANGE
          ) {
            filter.value = { min: null, max: null }
          } else {
            filter.value = null
          }
        }
        this.$emit("update:filters", this.innerFilters)
      },
      close() {
        this.isOpen = false
      },
      validateDateRange(filterName: string | number, changed: "from" | "to") {
        const filter = this.innerFilters[filterName] as FilterDateRange
        const { from, to } = filter.value

        if (!from || !to) return

        if (changed === "from" && dayjs(from).isAfter(dayjs(to))) {
          filter.value.to = null
        } else if (changed === "to" && dayjs(to).isBefore(dayjs(from))) {
          filter.value.from = null
        }
      },
      validateNumberRange(filterName: string | number, changed: "min" | "max") {
        const filter = this.innerFilters[filterName] as
          FilterMoneyRange | FilterNumberRange
        const { min, max } = filter.value

        if (!min || !max) return

        if (changed === "min" && min > max) {
          filter.value.max = null
        } else if (changed === "max" && max < min) {
          filter.value.min = null
        }
      },
    },
  })
</script>
<style scoped>
  .so-data-table-filters__list {
    max-height: 70vh;
    border-bottom: 1px solid rgb(var(--v-theme-border));
    overflow-y: auto;
    border-radius: 0;
    min-width: 360px;

    :deep(.v-field--variant-solo) {
      min-width: 0;
    }
  }
  .so-data-table-filters__option {
    flex: 1 0 100%;
  }
</style>
